import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import FAQAccordion from '../components/FAQAccordion';
import { fetchProducts } from '../api/productsApi';
import { placeholderSvgDataUrl } from '../utils/placeholderImage';
import { API_BASE } from '../config/api';

const Category = () => {
  const { categoryName } = useParams();
  
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        // Fetch categories to find the current one
        const catRes = await fetch(`${API_BASE}/categories`);
        const cats = await catRes.json();
        const currentCat = cats.find(c => c.slug === categoryName);
        
        if (mounted) setCategory(currentCat);

        const data = await fetchProducts({ active: true, category: categoryName });
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load products');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [categoryName]);

  const displayProducts = useMemo(() => items, [items]);

  const faqs = [
    {
      question: `What are the benefits of ${category?.name || 'these products'}?`,
      answer: `${category?.name || 'These products'} offer superior energy efficiency, longer lifespan, and better light quality compared to traditional lighting solutions. They are designed to provide optimal illumination while reducing energy costs.`
    },
    {
      question: "Are these products suitable for commercial use?",
      answer: "Yes, all our products are designed for commercial applications including retail spaces, offices, warehouses, and industrial facilities. They meet all relevant safety and quality standards."
    },
    {
      question: "What is the warranty period?",
      answer: "We offer comprehensive warranty coverage ranging from 2 to 5 years depending on the product category. All products come with a standard 2-year warranty with extended options available."
    },
    {
      question: "Do you provide installation services?",
      answer: "Yes, we provide professional installation services through our network of certified technicians. Contact us for a customized installation quote."
    },
    {
      question: "What payment options are available?",
      answer: "We accept various payment methods including bank transfers, credit/debit cards, and offer flexible payment terms for bulk orders and distributors."
    }
  ];

  if (!category) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <h1>Category Not Found</h1>
        <p>The category you're looking for doesn't exist.</p>
        <Link to="/products" className="btn">Browse All Products</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Category Header */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>{category.name}</h1>
            <p>{category.description}</p>
            <p style={{ fontSize: '1rem', color: '#999', marginTop: '1rem' }}>
              {loading ? 'Loading products...' : `${displayProducts.length} Products Available`}
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section>
        <div className="container">
          {err && (
            <div className="card" style={{ borderColor: '#5b1a1a' }}>
              <div style={{ color: '#ffb3b3' }}>{err}</div>
            </div>
          )}
          <div className="grid grid-3" style={{ 
            gap: '1.5rem'
          }}>
            {!loading &&
              displayProducts.map((product) => (
              <Link 
                key={product.id}
                to={`/product/${product.id}`}
                style={{ 
                  textDecoration: 'none', 
                  color: 'inherit',
                  display: 'block'
                }}
              >
                <div className="card" style={{ 
                  padding: '0', 
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '12px'
                }}>
                  <img 
                    src={
                      (product.images && product.images[0]) ||
                      placeholderSvgDataUrl({ width: 800, height: 520, text: product.title || 'Product' })
                    }
                    alt={product.title}
                    style={{ 
                      width: '100%', 
                      height: '200px', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
                    padding: '1.5rem'
                  }}>
                    <h3 style={{ color: '#fff', marginBottom: '0.25rem', fontSize: '1.1rem', fontWeight: '600' }}>
                      {product.title}
                    </h3>
                    <p style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: '1.2', marginBottom: '0.5rem' }}>
                      {product.description}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--accent)', fontSize: '0.95rem', fontWeight: 'bold' }}>
                        &#8377;{Number(product.price || 0).toLocaleString()}
                      </span>
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '15px', 
                        backgroundColor: 'var(--accent)', 
                        color: 'var(--primary)', 
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'inline-block'
                      }}>
                        View
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {!loading && displayProducts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <p style={{ color: '#999', fontSize: '1.1rem' }}>
                No products available in this category at the moment.
              </p>
              <Link to="/products" className="btn" style={{ marginTop: '1rem' }}>
                Browse Other Categories
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* SEO Content */}
      <section style={{ backgroundColor: 'var(--secondary)' }}>
        <div className="container">
          <h2 className="section-title">About {category.name}</h2>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <p style={{ lineHeight: '1.8', color: '#ccc', marginBottom: '1.5rem' }}>
              {category.name} from FACTO ELECTRONICAL represent the pinnacle of LED lighting technology, 
              designed specifically for the Indian market. Our products combine cutting-edge innovation 
              with reliable performance to deliver exceptional lighting solutions for various applications.
            </p>
            <p style={{ lineHeight: '1.8', color: '#ccc', marginBottom: '1.5rem' }}>
              Each product in our {category.name.toLowerCase()} collection undergoes rigorous testing 
              to ensure it meets international quality standards. We source premium components and 
              employ advanced manufacturing processes to create lighting solutions that are not only 
              energy-efficient but also built to last.
            </p>
            <p style={{ lineHeight: '1.8', color: '#ccc' }}>
              Whether you're a retailer, distributor, or end-user, our {category.name.toLowerCase()} 
              offer the perfect balance of performance, efficiency, and value. Backed by our comprehensive 
              warranty and expert support, you can trust FACTO ELECTRONICAL for all your lighting needs.
            </p>
          </div>
          
          <div className="grid grid-3" style={{ 
            marginTop: '3rem'
          }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <h4 style={{ color: 'var(--accent)', marginBottom: '1rem' }}>Quality Assurance</h4>
              <p style={{ color: '#ccc', fontSize: '0.95rem' }}>
                All products tested for quality and performance
              </p>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <h4 style={{ color: 'var(--accent)', marginBottom: '1rem' }}>Energy Efficient</h4>
              <p style={{ color: '#ccc', fontSize: '0.95rem' }}>
                Reduce energy costs by up to 75%
              </p>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <h4 style={{ color: 'var(--accent)', marginBottom: '1rem' }}>Expert Support</h4>
              <p style={{ color: '#ccc', fontSize: '0.95rem' }}>
                Professional guidance and assistance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section>
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about our {category.name.toLowerCase()}</p>
          
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h2>Need Help with {category.name}?</h2>
            <p>Our experts are here to help you choose the right lighting solution</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn">Get Expert Advice</Link>
              <Link to="/media" className="btn-secondary">View Projects</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Category;
